import React, { useState, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { api } from '../services/authService'

const PaymentMethodForm = ({ paymentMethod, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'cash',
    installments_allowed: false,
    max_installments: 1,
    days_to_receive: 0,
    fee_percentage: 0
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (paymentMethod) {
      setFormData({
        name: paymentMethod.name || '',
        type: paymentMethod.type || 'cash',
        installments_allowed: Boolean(paymentMethod.installments_allowed),
        max_installments: paymentMethod.max_installments || 1,
        days_to_receive: paymentMethod.days_to_receive || 0,
        fee_percentage: paymentMethod.fee_percentage || 0
      })
    }
  }, [paymentMethod])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Ajustar dados para o backend
      const dataToSend = {
        ...formData,
        installments_allowed: formData.installments_allowed ? 1 : 0,
        max_installments: formData.installments_allowed ? formData.max_installments : 1
      }

      if (paymentMethod) {
        await api.put(`/api/payment-methods/${paymentMethod.id}`, dataToSend)
      } else {
        await api.post('/api/payment-methods', dataToSend)
      }

      toast.success(
        paymentMethod
          ? 'Forma de pagamento atualizada com sucesso!'
          : 'Forma de pagamento criada com sucesso!'
      )
      onSuccess()
    } catch (error) {
      console.error('Erro ao salvar forma de pagamento:', error)
      const message = error.response?.data?.error || 'Erro ao salvar forma de pagamento'
      toast.error(message)
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.')
      }
    } finally {
      setLoading(false)
    }
  }

  const paymentTypes = [
    { value: 'cash', label: 'Dinheiro' },
    { value: 'pix', label: 'PIX' },
    { value: 'debit_card', label: 'Cartão de Débito' },
    { value: 'credit_card', label: 'Cartão de Crédito' },
    { value: 'bank_slip', label: 'Boleto Bancário' },
    { value: 'bank_transfer', label: 'Transferência Bancária' }
  ]

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {paymentMethod ? 'Editar Forma de Pagamento' : 'Nova Forma de Pagamento'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nome */}
            <div className="md:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Nome *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: Cartão de Crédito Visa"
              />
            </div>

            {/* Tipo */}
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                Tipo *
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {paymentTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Dias para Receber */}
            <div>
              <label htmlFor="days_to_receive" className="block text-sm font-medium text-gray-700 mb-1">
                Dias para Receber
              </label>
              <input
                type="number"
                id="days_to_receive"
                name="days_to_receive"
                value={formData.days_to_receive}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Quantos dias até receber o pagamento (0 = imediato)
              </p>
            </div>

            {/* Taxa */}
            <div>
              <label htmlFor="fee_percentage" className="block text-sm font-medium text-gray-700 mb-1">
                Taxa (%)
              </label>
              <input
                type="number"
                id="fee_percentage"
                name="fee_percentage"
                value={formData.fee_percentage}
                onChange={handleChange}
                min="0"
                max="100"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 mt-1">
                Taxa cobrada pela operadora (ex: 3.5)
              </p>
            </div>

            {/* Permite Parcelamento */}
            <div className="md:col-span-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="installments_allowed"
                  name="installments_allowed"
                  checked={formData.installments_allowed}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="installments_allowed" className="ml-2 block text-sm text-gray-700">
                  Permite parcelamento
                </label>
              </div>
            </div>

            {/* Máximo de Parcelas - só aparece se parcelamento estiver habilitado */}
            {formData.installments_allowed && (
              <div>
                <label htmlFor="max_installments" className="block text-sm font-medium text-gray-700 mb-1">
                  Máximo de Parcelas
                </label>
                <input
                  type="number"
                  id="max_installments"
                  name="max_installments"
                  value={formData.max_installments}
                  onChange={handleChange}
                  min="1"
                  max="24"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>

          {/* Informações Adicionais */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Informações:</h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• O nome deve ser único e descritivo</li>
              <li>• Taxa será aplicada automaticamente nos cálculos</li>
              <li>• Dias para receber afeta o fluxo de caixa</li>
              {formData.installments_allowed && (
                <li>• Parcelamento disponível para esta forma de pagamento</li>
              )}
            </ul>
          </div>

          {/* Botões */}
          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Salvando...' : (paymentMethod ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PaymentMethodForm